import { useState } from 'react';
import { ChevronDown, Menu } from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import type { NavigateOptions } from '@tanstack/react-router';

import {
    NavigationMenu,
    NavigationMenuList,
    NavigationMenuItem,
    NavigationMenuTrigger,
    NavigationMenuContent,
    NavigationMenuLink,
} from '~/primitives/navigation-menu';

import { Button } from '~/primitives/button';
import { Separator } from '~/primitives/separator';
import { Switch } from '~/primitives/switch';
import {
    Collapsible,
    CollapsibleTrigger,
    CollapsibleContent,
} from '~/primitives/collapsible';

import { cn } from '~/util';
import { useAuthData, useScopes } from './Providers/AuthProvider';
import { useSettingsStore } from '~/util/stores/settings';
import type { Permission } from '~/util/types/permissions';

type NavLeaf = {
    type: 'link';
    label: string;
    to: NavigateOptions;
    requireAuth?: boolean;
    requireScope?: Permission | null;
};

type NavSeparator = { type: 'separator'; key: string };

type NavGroup = {
    type: 'group';
    label: string;
    requireAuth?: boolean;
    requireScope?: Permission | null;
    items: Array<NavLeaf | NavSeparator>;
};

type NavNode = NavLeaf | NavGroup;

const nav: NavNode[] = [
    { type: 'link', label: 'DEMON LIST', to: { to: '/list' } },
    { type: 'link', label: 'LEADERBOARD', to: { to: '/leaderboard' } },
    { type: 'link', label: 'PACKS', to: { to: '/packs' } },
    {
        type: 'group',
        label: 'CHALLENGES/GAMES',
        items: [
            {
                type: 'link',
                label: 'BOUNTY BOARD',
                to: { to: '/games/bounty-board' },
            },
            { type: 'separator', key: 'games-gd' },
            { type: 'link', label: 'ROULETTE', to: { to: '/games/roulette' } },
            { type: 'link', label: 'ALPHABET', to: { to: '/games/alphabet' } },
            { type: 'separator', key: 'games-site' },
            {
                type: 'link',
                label: 'AREDLE',
                to: { to: '/games/aredle' },
            },
            {
                type: 'link',
                label: 'HIGHER OR LOWER',
                to: { to: '/games/higher-lower' },
            },
            {
                type: 'link',
                label: 'WHEEL SPINNER',
                to: { to: '/games/wheel-spinner' },
            },
        ],
    },
    { type: 'link', label: 'SUBMIT A RECORD', to: { to: '/submissions' } },
    {
        type: 'group',
        label: 'STAFF',
        requireAuth: true,
        requireScope: null,
        items: [
            {
                type: 'link',
                label: 'SUBMISSIONS',
                to: { to: '/staff/submissions' },
            },
            {
                type: 'link',
                label: 'RECORDS',
                to: { to: '/staff/records' },
                requireScope: 'record_modify',
            },
            { type: 'link', label: 'SHIFTS', to: { to: '/staff/shifts' } },
            { type: 'separator', key: 'staff-list' },
            { type: 'link', label: 'USERS', to: { to: '/staff/users' } },
            { type: 'link', label: 'CLANS', to: { to: '/staff/clans' } },
            {
                type: 'link',
                label: 'MERGES',
                to: { to: '/staff/merges' },
                requireScope: 'merge_review',
            },
            {
                type: 'link',
                label: 'ROLES',
                to: { to: '/staff/roles' },
                requireScope: 'role_assign',
            },
            { type: 'separator', key: 'staff-global' },
            {
                type: 'link',
                label: 'STATS',
                to: { to: '/staff/stats' },
                requireScope: 'submission_see_statistics',
            },
        ],
    },
];

function topButtonClass(active: boolean) {
    return cn(
        'text-lg font-semibold',
        active
            ? 'text-primary-foreground bg-primary hover:bg-primary hover:text-primary-foreground'
            : 'text-secondary-light',
    );
}

function desktopTriggerClass(active: boolean) {
    return cn(
        'bg-transparent text-lg font-semibold hover:scale-105 hover:shadow-xl transition-all',
        active
            ? 'bg-primary text-primary-foreground hover:bg-primary'
            : 'text-secondary-light',
    );
}

function DesktopLink({
    leaf,
    active,
    onClick,
}: {
    leaf: NavLeaf;
    active: boolean;
    onClick?: () => void;
}) {
    return (
        <NavigationMenuItem>
            <NavigationMenuLink asChild>
                <Button
                    asChild
                    variant="ghost"
                    className={topButtonClass(active)}
                >
                    <Link
                        {...leaf.to}
                        onClick={onClick}
                        preload="intent"
                        preloadDelay={200}
                    >
                        {leaf.label}
                    </Link>
                </Button>
            </NavigationMenuLink>
        </NavigationMenuItem>
    );
}

function DesktopGroup({
    group,
    onClick,
    shouldDisplay,
}: {
    group: NavGroup;
    onClick?: () => void;
    shouldDisplay: (node?: NavNode) => boolean;
}) {
    return (
        <NavigationMenuItem className="relative">
            <NavigationMenuTrigger className={desktopTriggerClass(false)}>
                {group.label}
            </NavigationMenuTrigger>
            <NavigationMenuContent className="min-w-55 flex flex-col rounded-xl shadow-md font-medium bg-background-light p-2 border">
                {group.items.map((item) => {
                    if (item.type === 'separator')
                        return <Separator key={item.key} className="my-px" />;
                    if (!shouldDisplay(item)) return null;

                    return (
                        <Button
                            key={item.label}
                            asChild
                            variant="ghost"
                            className="justify-start text-lg font-semibold text-secondary-light"
                        >
                            <Link
                                {...item.to}
                                onClick={onClick}
                                preload="intent"
                                preloadDelay={200}
                            >
                                {item.label}
                            </Link>
                        </Button>
                    );
                })}
            </NavigationMenuContent>
        </NavigationMenuItem>
    );
}

function MobileLink({
    leaf,
    onClick,
}: {
    leaf: NavLeaf;
    onClick?: () => void;
}) {
    return (
        <Button
            asChild
            variant="ghost"
            className="w-full justify-end text-lg font-semibold text-secondary-light"
        >
            <Link {...leaf.to} onClick={onClick}>
                {leaf.label}
            </Link>
        </Button>
    );
}

function MobileGroup({
    group,
    open,
    onOpenChange,
    onClick,
    shouldDisplay,
}: {
    group: NavGroup;
    open: boolean;
    onOpenChange: (v: boolean) => void;
    onClick?: () => void;
    shouldDisplay: (node?: NavNode) => boolean;
}) {
    return (
        <Collapsible open={open} onOpenChange={onOpenChange} className="w-full">
            <CollapsibleTrigger asChild>
                <button
                    type="button"
                    className="w-full px-4 py-2 flex items-center justify-end gap-2 text-lg font-semibold text-secondary-light min-w-0"
                >
                    <span className="min-w-0 truncate text-right text-secondary-light">
                        {group.label}
                    </span>
                    <ChevronDown
                        className={cn(
                            'h-4 w-4 transition-transform shrink-0',
                            open && 'rotate-180',
                        )}
                    />
                </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <div className="flex flex-col items-end gap-1 py-1 border-r mr-2">
                    {group.items.map((item) => {
                        if (item.type === 'separator')
                            return (
                                <Separator key={item.key} className="my-px" />
                            );
                        if (!shouldDisplay(item)) return null;
                        return (
                            <MobileLink
                                key={item.label}
                                leaf={item}
                                onClick={onClick}
                            />
                        );
                    })}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

function Navbar() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileGroups, setMobileGroups] = useState<Record<string, boolean>>(
        {},
    );

    const pathname = useLocation({ select: ({ pathname }) => pathname });

    const authData = useAuthData();
    const { scopes, hasScope } = useScopes();
    const { staff_mode, setStaffMode } = useSettingsStore();

    const shouldDisplay = (node?: NavNode) =>
        node?.requireAuth && !authData?.username
            ? false
            : node?.requireScope === null
              ? hasScope(undefined)
              : node?.requireScope !== undefined
                ? hasScope(node.requireScope)
                : true;

    const closeMobileMenu = () => {
        setMobileOpen(false);
    };

    const isTopActive = (to?: NavigateOptions) => {
        const a = to?.to?.split('/')[1];
        const b = pathname.split('/')[1];
        return a != undefined && b != undefined && a === b;
    };

    const visibleNav = nav.filter(shouldDisplay);

    const mobileAuthedLinks: NavLeaf[] = authData?.username
        ? [
              {
                  type: 'link',
                  label: 'PROFILE',
                  to: {
                      to: '/profile/user/$userid',
                      params: { userid: authData?.username },
                  },
              },
              { type: 'link', label: 'CLAN', to: { to: '/clan' } },
              { type: 'link', label: 'SETTINGS', to: { to: '/settings' } },
              {
                  type: 'link',
                  label: 'LOGOUT',
                  to: { to: '/logout', search: { redirectTo: pathname } },
              },
          ]
        : [{ type: 'link', label: 'LOGIN', to: { to: '/login' } }];

    return (
        <nav className="text-secondary-light font-semibold uppercase">
            <div className="xl:hidden flex items-center justify-end p-4">
                <button
                    type="button"
                    onClick={() => setMobileOpen((o) => !o)}
                    className="cursor-pointer transition-transform hover:scale-110"
                    aria-label="Toggle navigation"
                >
                    <Menu size={32} />
                </button>
            </div>

            {/* Desktop */}
            <div className="hidden xl:flex items-center justify-start">
                <NavigationMenu className="justify-start">
                    <NavigationMenuList className="flex flex-row items-center gap-1">
                        {visibleNav.map((node) =>
                            node.type === 'link' ? (
                                <DesktopLink
                                    key={node.label}
                                    leaf={node}
                                    active={isTopActive(node.to)}
                                    onClick={closeMobileMenu}
                                />
                            ) : (
                                <DesktopGroup
                                    key={node.label}
                                    group={node}
                                    onClick={closeMobileMenu}
                                    shouldDisplay={shouldDisplay}
                                />
                            ),
                        )}
                    </NavigationMenuList>
                </NavigationMenu>
            </div>

            {/* Mobile */}
            <div
                className={cn(
                    'xl:hidden w-full overflow-hidden grid transition-[grid-template-rows,opacity] duration-300 ease-out',
                    mobileOpen
                        ? 'grid-rows-[1fr] opacity-100'
                        : 'grid-rows-[0fr] opacity-0',
                )}
            >
                <div className="min-h-0">
                    <div className="flex flex-col items-end gap-1">
                        {visibleNav.map((node) =>
                            node.type === 'link' ? (
                                <MobileLink
                                    key={node.label}
                                    leaf={node}
                                    onClick={closeMobileMenu}
                                />
                            ) : (
                                <MobileGroup
                                    key={node.label}
                                    group={node}
                                    open={mobileGroups[node.label] ?? false}
                                    onOpenChange={(v) =>
                                        setMobileGroups((s) => ({
                                            ...s,
                                            [node.label]: v,
                                        }))
                                    }
                                    onClick={closeMobileMenu}
                                    shouldDisplay={shouldDisplay}
                                />
                            ),
                        )}

                        <Separator className="my-4" />

                        {authData?.username && scopes.length > 0 && (
                            <div className="flex items-center justify-between px-4 py-2 w-full">
                                <Switch
                                    checked={staff_mode}
                                    onCheckedChange={setStaffMode}
                                />
                                <span className="text-secondary-light">
                                    {staff_mode ? 'STAFF MODE' : 'USER MODE'}
                                </span>
                            </div>
                        )}

                        {mobileAuthedLinks.map((leaf) => (
                            <MobileLink
                                key={leaf.label}
                                leaf={leaf}
                                onClick={closeMobileMenu}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </nav>
    );
}

export default Navbar;
